import { useCallback, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Field } from '@opendoor/atoms/field';
import { Input, type TextInputProps } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { centsToDollars } from 'components/marketplace/helpers/money';
import type { ErrorKey, FilterErrors } from 'components/marketplace/search/types';
import { formatCurrencyDollars, parseCurrencyInputToCents } from 'helpers/formatters';

const MIN_PRICE_DOLLARS = 1;
const MAX_PRICE_DOLLARS = 100_000_000;
const UNSIGNED_CURRENCY_INPUT_PATTERN = /^[\d$,\s]*$/;
const DIGIT_KEY_PATTERN = /^\d$/;

type CurrencyFilterInputProps = Pick<TextInputProps, 'aria-label' | 'error' | 'placeholder'> & {
  onValueChange: (value: number | null) => void;
  value?: number | null;
};

type PriceErrorKey = Extract<ErrorKey, 'minPrice' | 'maxPrice'>;

interface PriceRange {
  maxPrice: number | null;
  minPrice: number | null;
}

interface InvalidPriceDraft extends PriceRange {
  baseMaxPrice: number | null;
  baseMinPrice: number | null;
}

const normalizePrice = (value: number | null): number | null =>
  value == null || value < MIN_PRICE_DOLLARS
    ? null
    : Math.min(Math.trunc(value), MAX_PRICE_DOLLARS);

const parseWholeDollarAmount = (input: string): number | null =>
  normalizePrice(centsToDollars(parseCurrencyInputToCents(input)));

const CurrencyFilterInput = ({ onValueChange, value, ...inputProps }: CurrencyFilterInputProps) => {
  const handleChange = useCallback<NonNullable<TextInputProps['onChange']>>(
    (event) => {
      const input = event.currentTarget.value;
      if (!UNSIGNED_CURRENCY_INPUT_PATTERN.test(input)) {
        return;
      }
      onValueChange(parseWholeDollarAmount(input));
    },
    [onValueChange],
  );

  const handleKeyDown = useCallback<NonNullable<TextInputProps['onKeyDown']>>((event) => {
    if (
      event.key.length === 1 &&
      !DIGIT_KEY_PATTERN.test(event.key) &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey
    ) {
      event.preventDefault();
    }
  }, []);

  return (
    <Input
      {...inputProps}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={value == null ? '' : formatCurrencyDollars(value)}
      size="sm"
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  );
};

export interface PriceFilterProps {
  minPrice?: number | null;
  maxPrice?: number | null;
  handleMinPriceChange: (value: number | null) => void;
  handleMaxPriceChange: (value: number | null) => void;
  handleError: (key: ErrorKey, val: string) => void;
  errors?: FilterErrors | null;
}

export const PriceFilter = ({
  minPrice,
  maxPrice,
  handleMinPriceChange,
  handleMaxPriceChange,
  handleError,
  errors,
}: PriceFilterProps) => {
  const normalizedMinPrice = minPrice ?? null;
  const normalizedMaxPrice = maxPrice ?? null;
  const [invalidDraft, setInvalidDraft] = useState<InvalidPriceDraft | null>(null);
  const activeInvalidDraft =
    invalidDraft?.baseMinPrice === normalizedMinPrice &&
    invalidDraft.baseMaxPrice === normalizedMaxPrice
      ? invalidDraft
      : null;
  const draftMinPrice = activeInvalidDraft?.minPrice ?? normalizedMinPrice;
  const draftMaxPrice = activeInvalidDraft?.maxPrice ?? normalizedMaxPrice;

  const commitRange = useCallback(
    (nextRange: PriceRange, errorKey: PriceErrorKey) => {
      const oppositeErrorKey = errorKey === 'minPrice' ? 'maxPrice' : 'minPrice';
      handleError(oppositeErrorKey, '');
      if (
        nextRange.minPrice != null &&
        nextRange.maxPrice != null &&
        nextRange.minPrice > nextRange.maxPrice
      ) {
        setInvalidDraft({
          ...nextRange,
          baseMinPrice: normalizedMinPrice,
          baseMaxPrice: normalizedMaxPrice,
        });
        handleError(
          errorKey,
          errorKey === 'minPrice'
            ? 'Min price must be less than max price'
            : 'Max price must be greater than min price',
        );
        return;
      }

      setInvalidDraft(null);
      handleError(errorKey, '');
      if (nextRange.minPrice !== normalizedMinPrice) {
        handleMinPriceChange(nextRange.minPrice);
      }
      if (nextRange.maxPrice !== normalizedMaxPrice) {
        handleMaxPriceChange(nextRange.maxPrice);
      }
    },
    [
      handleError,
      handleMaxPriceChange,
      handleMinPriceChange,
      normalizedMaxPrice,
      normalizedMinPrice,
    ],
  );

  const onMinPriceChange = useCallback(
    (value: number | null) => {
      commitRange({ minPrice: value, maxPrice: draftMaxPrice }, 'minPrice');
    },
    [commitRange, draftMaxPrice],
  );

  const onMaxPriceChange = useCallback(
    (value: number | null) => {
      commitRange({ minPrice: draftMinPrice, maxPrice: value }, 'maxPrice');
    },
    [commitRange, draftMinPrice],
  );

  return (
    <Col className="w-full gap-5">
      <Text as="p" className="text-label-xl">
        Price range
      </Text>
      <Row className="w-full items-start justify-between gap-3">
        <Col className="min-w-0 flex-1 gap-2">
          <Text as="span" className="text-label-sm text-od-content-secondary">
            Min
          </Text>
          <Field error={errors?.['minPrice'] || undefined}>
            <CurrencyFilterInput
              aria-label="Minimum price"
              placeholder="No min"
              value={draftMinPrice}
              error={Boolean(errors?.['minPrice'])}
              onValueChange={onMinPriceChange}
            />
          </Field>
        </Col>
        <Col className="min-w-0 flex-1 gap-2">
          <Text as="span" className="text-label-sm text-od-content-secondary">
            Max
          </Text>
          <Field error={errors?.['maxPrice'] || undefined}>
            <CurrencyFilterInput
              aria-label="Maximum price"
              placeholder="No max"
              value={draftMaxPrice}
              error={Boolean(errors?.['maxPrice'])}
              onValueChange={onMaxPriceChange}
            />
          </Field>
        </Col>
      </Row>
    </Col>
  );
};
